
import { Enums, IEnums } from '../../enums/enums';
import { } from '../cache/localCache.service';
import { LocaleService } from '../../locale/locale.service';
import { Injectable } from '@angular/core';
import * as Models from '../../models/models-index';
import { cultureCodes } from '../../constants/constants'
import { Subject, Observable, BehaviorSubject, EMPTY } from 'rxjs';
import { AuthenticationService } from '../authentication/authentication.service';
import { tap, map, switchMap, filter, startWith } from 'rxjs/operators';
import { AppState } from '../../../_store/app-state.model';
import { Store, select } from '@ngrx/store';
import { AppSelectors } from '../../../_store/selector-types';
import { ConfigurationService } from '../config/config.service';
import { FilterActions } from '../../filter/store/action-types';
import { FilterSelectors } from '../../filter/store';
import { TypeCheck } from '../../hy-filter-panel-module/Utils';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ReportViewFilterSelection, SelectedFilterModel } from '../../models/models-index';
import { coerceArray } from '@angular/cdk/coercion';

export interface IFilterDates {
  startDate: Date;
  endDate: Date;
  previousStartDate: Date;
  previousEndDate: Date;
}

export interface IDaterangeRestrictions {
  restrict: boolean;
  numberOfMonthsToShow: number;
}

@Injectable({ providedIn: 'root'})
export class FilterService {

  private enums: IEnums = Enums;
  filterName: string;
  activePageTitle$ = new BehaviorSubject('');

  orgLookups: Models.IOrgLookup[];
  currentSalesMonth: Date;
  dayOfSalesMonth: number;
  locale: string;

  private secondaryNavResized = new Subject<string>();
  private daterangeRestrictions = new Subject<IDaterangeRestrictions>();

  secondaryNavResized$ = this.secondaryNavResized.asObservable();
  daterangeRestrictions$ = this.daterangeRestrictions.asObservable();

  currentFilterModel: Models.IFilterModel;
  defaultFilter: Models.IFilterModel;

  // Observables for requesting and returning filter breadcrumbs
  private filterBreadcrumbsRequested = new Subject<void>();
  private filterBreadcrumbsReturned = new Subject<string[]>();
  filterBreadcrumbsRequested$ = this.filterBreadcrumbsRequested.asObservable();
  filterBreadcrumbsReturned$ = this.filterBreadcrumbsReturned.asObservable();

  filter$: Observable<Models.IFilterModel>;
  currentReportName$: Observable<string>;

  private isRouteStable$ = this.router.events.pipe(
    filter(e => e instanceof NavigationStart || e instanceof NavigationEnd || e instanceof NavigationCancel),
    map(e => e instanceof NavigationEnd || e instanceof NavigationCancel),
    startWith(true),
  );

  // Auth/User info
  private authInfo: Models.IAuthenticationInfo;

  constructor(
    private store: Store<AppState>,
    private localeService: LocaleService,
    private authService: AuthenticationService,
    private configService: ConfigurationService,
    private router: Router) {

    // this.filter$ = this.isRouteStable$.pipe(
    //   switchMap(stable => !stable ? EMPTY : this.store.select(AppSelectors.selectCurrentRouteData)),
    //   filter(routeData => !routeData.useV5Filters),
    //   switchMap(routeData => this.filterBarService.getReportFilterModel(routeData.reportName))
    // );


    this.localeService.locale$.pipe(tap(loc => {
      this.locale = loc;
    })).subscribe(),

      this.currentReportName$ = this.store.select(AppSelectors.selectCurrentRouteData).pipe(
        map(data => data.reportName || 'UNKNOWN')
      );
  }

  initialize() {
    this.authInfo = this.authService.getUser();
  }

  getCurrentFilterModel() {
    return this.currentFilterModel;
  }

  getDefaultFilterModel() {
    return this.defaultFilter;
  }

  setActivePageTitle(title: string) {
    this.activePageTitle$.next(title);
  }


  restrictDaterangeOptions(options: IDaterangeRestrictions) {
    this.daterangeRestrictions.next(options);
  }

  triggerNavResizeEvent(filterState: string) {
    this.secondaryNavResized.next(filterState);
  }

  // Requesting/returning filter breadcrumbs
  requestBreadcrumbString() {
    this.filterBreadcrumbsRequested.next();
  }

  returnBreadcrumbString(breadcrumbs: string[]) {
    this.filterBreadcrumbsReturned.next(breadcrumbs);
  }

  convertStringToStringArray(value: string): string[] {
    if (!value) {
      return null;
    } else {
      const array = value.split(',');
      return array;
    }
  }

  convertStringToNumberArray(value: string): number[] {
    if (!value) {
      return null;
    } else {
      const array = value.split(',').map(Number);
      return array;
    }
  }

  // initializeReportViewFilters(reportName: string, reportViewFilters: Models.ReportViewFilter[]) {
  //   this.store.dispatch(FilterActions.initializeReportViewFilters({ reportName, filters: reportViewFilters }));
  // }

  updateReportViewFilter(reportName: string, selection: ReportViewFilterSelection) {
    this.store.dispatch(FilterActions.updateReportViewFilterSelection({ reportName, selection }));
  }

  updateReportViewFilters(reportName: string, selections: ReportViewFilterSelection[]) {
    this.store.dispatch(FilterActions.updateReportViewFilterSelections({ reportName, selections }));
  }

  getReportViewFilters(reportName: string) {
    return this.store.select(FilterSelectors.selectReportViewFiltersForReport({ reportName }));
  }

  getReportViewFilterSelection(reportName: string, filterName: string) {
    return this.store.select(FilterSelectors.selectReportViewFilterSelection({ reportName, filterName }));
  }

  getReportViewFilterSelections(reportName: string) {
    return this.store.select(FilterSelectors.selectReportViewFilterSelections({ reportName }));
  }

  getReportViewFilterRequestModel(reportName: string): Observable<Models.ReportRequestModel> {
    return this.store.select(FilterSelectors.selectReportViewFilterSelections({ reportName })).pipe(
      filter(selections => ((selections || []).length > 0)),
      // tap(selections => console.log('[filter.service getReportViewFilterRequestModel] selections: ', selections)),
      map(selections => {
        const requestModel = <Models.ReportRequestModel>{
          reportType: reportName,
          filters: {
            culture: this.locale == 'en' ? cultureCodes.English : this.locale,
            roleLevel: this.authService.getUserDetails().role.name
          }
        };
        const requestFilterSelections: Models.ReportFilterRequestSelection[] = [];
        selections?.forEach(selection => {
          const name = selection.item.name;

          requestFilterSelections.push(this.convertToReportFilterValue(selection));

        });

        return {...requestModel, filterSelections: requestFilterSelections};
      })
    );
  }

  getDynamicLinkUrl(viewContext: Models.ViewContext, urlTemplate: string, keyMap: string): Observable<string> {
    if (!urlTemplate) {
      throw new Error('[FilterService] urlTemplate is required');
    }

    if (!!viewContext.legacyReport) {
      return this.filter$.pipe(
        map(filter => this.buildLegacyFilterUrl(filter, urlTemplate, keyMap))
        // tap(url => console.log('legacy url: ', url))
      );
    } else {
      return this.getReportViewFilterSelections(viewContext.reportName).pipe(
        map(selections => this.buildFilterUrl(selections, urlTemplate, keyMap))
      );
    }
  }

  transposeFilterModelToParameterMap(
    keyMap: string,
    filterModel: Models.IFilterModel
  ): Record<string, string> {
      // Split the string into parts based on the comma
      const pairs = keyMap.split(',');

      const result: Record<string, any> = {};

      // Iterate over the pairs
      for (const pair of pairs) {
          // Remove square brackets and split by the pipe symbol
          const cleanedPair = pair.replace(/[\[\]]/g, ''); // Remove square brackets
          const [key, value] = cleanedPair.split('|');

          // Use the value to look up a property in the filter model
          if (filterModel[value] !== undefined) {
            if (value === 'startDate' || value === 'endDate') {
              //convert date to string formatted as 'YYYY-MM-DD'
              result[key] = new Date(filterModel[value]).toISOString().split('T')[0];
            }
            else
              result[key] = filterModel[value] ?? '';
          } else {
              result[key] = ''; // Handle missing properties gracefully
          }
      }

      return result;
  }

  replaceUrlTokens(url: string, dictionary: Record<string, any>): string {
      // Use a regular expression to match tokens in the format {tokenName}
      return url.replace(/\{([^}]+)\}/g, (match, token) => {
          // Replace the token with the value from the dictionary, or leave it as-is if not found
          return dictionary[token] !== undefined ? encodeURIComponent(dictionary[token]) : match;
      });
  }

  buildLegacyFilterUrl(filter: Models.IFilterModel, urlTemplate: string, keyMap: string): string {
    const parameterMap = this.transposeFilterModelToParameterMap(keyMap, filter);
    return this.replaceUrlTokens(urlTemplate, parameterMap);
  }

  getSelectionValueForUrl(selection: ReportViewFilterSelection): string[] {
    if (TypeCheck.isDateRangeValue(selection.item.value)) {
        return [selection.item.value.startDate,selection.item.value.endDate];
    } else if (TypeCheck.isHierarchyValueArray(selection.item.value)) {
        return [selection.item.value.map(h => h.key).join(',')];
    } else if (TypeCheck.isStringArray(selection.item.value)) {
        return [selection.item.value.join(',')];
    } else {
        throw new Error(`Unsupported filter value type: ${selection.item.value}`);
    }
  }

  transposeSelectionValuesToParameterMap(keyMap: string, selections: ReportViewFilterSelection[]): Record<string,string> {
    // Split the string into parts based on the comma
    const pairs = keyMap.split(',');

    const result: Record<string, any> = {};

    // Iterate over the pairs
    for (const pair of pairs) {
        // Remove square brackets and split by the pipe symbol
        const cleanedPair = pair.replace(/[\[\]]/g, ''); // Remove square brackets
        const [key, value] = cleanedPair.split('|');

        // Use the value to look up a property in the filter model
        if (value == 'startDate') {
          //get first selection that is of type date range and grab it's value's start date
          const selection = selections.find(s => TypeCheck.isDateRangeValue(s.item.value));
          if (selection && TypeCheck.isDateRangeValue(selection.item.value)) {
            result[key] = selection.item.value.startDate;
          }
        }
        else if (value == 'endDate') {
          //get first selection that is of type date range and grab it's value's end date
          const selection = selections.find(s => TypeCheck.isDateRangeValue(s.item.value));
          if (selection && TypeCheck.isDateRangeValue(selection.item.value)) {
            result[key] = selection.item.value.endDate;
          }
        }
        else {
          const selection = selections.find(s => s.name === value);
          if (selection) {
              result[key] = this.getSelectionValueForUrl(selection);
          } else {
              result[key] = null; // Handle missing properties gracefully
          }
        }
    }

    return result;
  }

  buildFilterUrl(selections: ReportViewFilterSelection[], urlTemplate: string, keyMap: string): string {
    const parameterMap = this.transposeSelectionValuesToParameterMap(keyMap, selections);
    return this.replaceUrlTokens(urlTemplate, parameterMap);
  }


  convertToReportFilterValue(filterSelection: ReportViewFilterSelection): Models.ReportFilterRequestSelection {
    if (TypeCheck.isDateRangeValue(filterSelection.item.value)) {
      // For date-range, keep path at the base filter (root) and use semantic label for name/display
      const fullPath = filterSelection.item.path || filterSelection.name;
      const rootPath = (fullPath || '').split('|')[0];
      const semantic = filterSelection.item.value.name || (fullPath.includes('|') ? fullPath.split('|').pop() : undefined) || filterSelection.name;
      return {
        name: filterSelection.name,
        value: {
          // Prefer semantic name for the date range (e.g., "This Month")
          name: semantic,
          // Use root path only (no trailing semantic segment)
          path: rootPath,
          startDate: filterSelection.item.value.startDate,
          endDate: filterSelection.item.value.endDate,
          kind: filterSelection.item.value.kind
        }
      };
    } else if (TypeCheck.isHierarchyValueArray(filterSelection.item.value)) {
      return {
        name: filterSelection.item.value[0].name,
        value: <Models.ReportFilterHierarchyValue>{
          name: filterSelection.item.value[0].name,
          hierarchyTypeId: filterSelection.item.value[0].hierarchyTypeId,
          hierarchyTypeName: filterSelection.item.value[0].hierarchyTypeName,
          value: filterSelection.item.value.map(h => h.key).join(",")
        }
      };
    } else if (TypeCheck.isStringArray(filterSelection.item.value)) {
      return {
        name: filterSelection.name,
        // Include comma string for legacy, but prefer high-fidelity expander below
        value: filterSelection.item.value.join(",")
      };
    } else {
      return {
        name: filterSelection.name,
        value: filterSelection.item.value
      };
    }
  }

  /**
   * High-fidelity mapping: expand a single UI selection into one-or-many API selections,
   * each with a path-aware value matching backend FilterValueBase shapes.
   * This avoids lossy CSV packing and preserves item.path to prevent duplicates during hydration.
   */
  expandSelectionToRequestSelections(selection: ReportViewFilterSelection): Models.ReportFilterRequestSelection[] {
    const root = (selection.item.path || selection.label || selection.name || '').split('|')[0];

    // Date range: one selection with explicit full path and semantic name
    if (TypeCheck.isDateRangeValue(selection.item.value)) {
      const v = selection.item.value;
      const fullPath = selection.item.path || selection.name;
      const semantic = v.name || (fullPath.includes('|') ? fullPath.split('|').pop() : undefined) || selection.name;
      return [{
        name: selection.name,
        value: <Models.ReportFilterDateRangeValue>{
          name: semantic,
          // Preserve full path
          path: fullPath,
          startDate: v.startDate,
          endDate: v.endDate,
          kind: v.kind
        }
      }];
    }

    // Hierarchy: one selection per chosen node, preserving key/value and path
    if (TypeCheck.isHierarchyValueArray(selection.item.value)) {
      const list = selection.item.value;
      return list.map(h => ({
        name: selection.name,
        value: <Models.ReportFilterHierarchyValue>{
          name: h.name || selection.name,
          path: h.path || selection.item.path || root,
          // Ensure key carries the node id/key; if root selection, use hierarchyTypeId
          key: (() => {
            const looksLikeGuid = (s?: string) => !!s && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(s);
            const isRoot = !((h?.path || '').includes('|')) || h?.value === root || (h?.value || '').toLowerCase() === 'national';
            if (looksLikeGuid(h.key)) return h.key;
            if (isRoot && h.hierarchyTypeId) return h.hierarchyTypeId;
            // Robustness: if key is empty but value looks like a GUID, treat that as the token
            if (looksLikeGuid(h.value as any)) return h.value as any;
            return h.key ?? '';
          })(),
          // Keep value as the display label if present
          value: h.value ?? h.key,
          hierarchyTypeId: h.hierarchyTypeId
        }
      }));
    }

    // Facet/string array: single selection with CSV string, include path for identity
    if (TypeCheck.isStringArray(selection.item.value)) {
      const csv = selection.item.value.join(',');
      return [{
        name: selection.name,
        value: <Models.ReportFilterSimpleValue><unknown>{
          name: selection.name,
          value: csv,
          display: csv,
          path: selection.item.path || root
        }
      }];
    }

    // Fallback: best-effort single selection, attach path if possible
    return [{
      name: selection.name,
      value: {
        ...(selection.item.value as any),
        path: (selection as any)?.item?.value?.path || root
      } as any
    }];
  }

  combineSelections(selections: Models.ReportViewFilterSelection[], newSelections: Models.ReportViewFilterSelection|Models.ReportViewFilterSelection[]): Models.ReportViewFilterSelection[] {
    return coerceArray(newSelections).reduce((combined, newSelection) => {
      const existingSelection = selections.find(s => rootPath(s) === rootPath(newSelection));

      return existingSelection
          ? combined.map(s => rootPath(s) === rootPath(newSelection) ? newSelection : s)
          : combined.concat(newSelection);
    }, selections);
  }

  /**
   * High-fidelity mapping from a UI selection to a Selected* model used for persistence and perfect rehydration.
   * Returns an array only for consistency with future multi-split scenarios; currently 1:1 per selection.
   */
  expandSelectionToSelectedModels(selection: Models.ReportViewFilterSelection): SelectedFilterModel[] {
    const root = (selection.item.path || selection.label || selection.name || '').split('|')[0];
    const baseItem = { name: selection.name, type: selection.item.type, path: selection.item.path || root, label: selection.label };

    if (TypeCheck.isDateRangeValue(selection.item.value)) {
      const v = selection.item.value;
      const fullPath = selection.item.path || selection.name;
      const semantic = v.name || (fullPath.includes('|') ? fullPath.split('|').pop() : undefined) || selection.name;
      return [{
        item: baseItem,
        dateRange: {
          name: semantic,
          path: v.path || fullPath,
          startDate: v.startDate,
          endDate: v.endDate,
          kind: (v as any).kind
        }
      }];
    }

    if (TypeCheck.isHierarchyValueArray(selection.item.value)) {
      const list = selection.item.value;
      const rootPath = selection.item.path || root;
      return [{
        item: baseItem,
        hierarchies: list.map(h => ({
          name: h.name,
          key: h.key,
          value: h.value,
          // Ensure a concrete leaf path for each selected hierarchy
          path: h.path || `${rootPath}|${h.key ?? h.value ?? h.name}`,
          hierarchyTypeId: h.hierarchyTypeId
        }))
      }];
    }

    if (TypeCheck.isStringArray(selection.item.value)) {
      // Facet/string filters: map each selected value to a facet with a unique path token
      const values = selection.item.value as string[];
      return [{
        item: baseItem,
        facets: values.map(v => ({
          name: String(v),
          path: `${selection.item.path || root}|${String(v)}`,
          value: String(v),
          label: String(v)
        }))
      } as any];
    }

    // Fallback: do not emit a Selected* when value type is unknown
    return [];
  }
}

function rootPath(selection: Models.ReportViewFilterSelection) {
  return selection.item.path.split('|')[0];
}
